import { useEffect, useState } from 'react'
import { useSwipeable } from 'react-swipeable'
import { apiClient } from '../api/client'
import { downloadImageWithExif } from '../utils/downloadImage'
import type { Image } from '../api/types'

type Props = {
  images: Image[]
  initialIndex: number
  onClose: () => void
}

/**
 * 画像モーダル
 *
 * 画像をフル表示。
 * ESCキーで閉じる、背景クリックで閉じる。
 * 矢印キーまたはスワイプで複数画像をナビゲート。
 */
export function ImageModal({ images, initialIndex, onClose }: Props) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const [blobUrl, setBlobUrl] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [downloading, setDownloading] = useState(false)
  const [downloadError, setDownloadError] = useState<string | null>(null)

  const currentImage = images[currentIndex]

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length)
  }

  const goToPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length)
  }

  const handleDownload = async () => {
    if (!blobUrl || !currentImage) return

    setDownloading(true)
    setDownloadError(null)

    try {
      await downloadImageWithExif(blobUrl, {
        date: currentImage.date,
        memberName: currentImage.memberName,
        key: currentImage.key,
      })
    } catch (error) {
      setDownloadError(
        error instanceof Error ? error.message : 'ダウンロードに失敗しました'
      )
    } finally {
      setDownloading(false)
    }
  }

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => goToNext(),
    onSwipedRight: () => goToPrev(),
    preventScrollOnSwipe: true,
    trackMouse: true,
    delta: 50,
    swipeDuration: 500,
  })

  useEffect(() => {
    if (!currentImage) return

    let cancelled = false

    const loadImage = async () => {
      try {
        setLoading(true)
        const url = await apiClient.fetchImageBlob(currentImage.key)
        if (!cancelled) {
          setBlobUrl(url)
          setLoading(false)
        }
      } catch (error) {
        console.error('Failed to load image:', error)
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    loadImage()

    return () => {
      cancelled = true
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl)
      }
    }
  }, [currentImage?.key, currentIndex])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      } else if (e.key === 'ArrowRight') {
        goToNext()
      } else if (e.key === 'ArrowLeft') {
        goToPrev()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [onClose, currentIndex])

  return (
    <div className="modal-overlay" onClick={onClose} {...swipeHandlers}>
      <button className="modal-close" onClick={onClose} aria-label="Close">
        ×
      </button>

      {images.length > 1 && (
        <div className="modal-pagination-counter">
          {currentIndex + 1} / {images.length}
        </div>
      )}

      {!loading && blobUrl && (
        <button
          className="modal-download-button"
          onClick={handleDownload}
          disabled={downloading}
          aria-label="画像をダウンロード"
        >
          {downloading ? (
            <span className="download-icon-loading">⏳</span>
          ) : (
            <span className="download-icon">⬇</span>
          )}
        </button>
      )}

      {downloadError && (
        <div className="modal-download-error">{downloadError}</div>
      )}

      {loading ? (
        <div className="modal-loading">Loading...</div>
      ) : blobUrl ? (
        <img
          src={blobUrl}
          alt={`Image ${currentIndex + 1} of ${images.length}`}
          className="modal-image"
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <div className="modal-error">Failed to load image</div>
      )}
    </div>
  )
}
